import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import { useNavigate, useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-contact/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modals/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import { 
  singleCountriesOptionsSelect,
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'
import { getPhoneCode } from 'src/helpers/format-number'

const EditContact = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)

  const [contactDetail, setContactDetail] = useState(null)

  const [countryOptions, setCountryOptions] = useState([])
  const [countryCodes, setCountryCodes] = useState([])
  const [country, setCountry] = useState(null)

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const formik = useFormik({
    initialValues: {
      contact_profile_name: contactDetail?.profile_name ? contactDetail?.profile_name : '',
      contact_company_name: contactDetail?.company_name ? contactDetail?.company_name : '',
      contact_wa_id: contactDetail?.wa_id ? contactDetail?.wa_id : '',
      contact_phone_code: '',
    },
    validationSchema: Yup.object().shape({
      contact_profile_name: Yup.string()
        .required("Please input profile name."),
      contact_company_name: Yup.string()
        .required("Please input company name."),
      contact_wa_id: Yup.string()
        .required("Please input whatsapp number."),
      contact_phone_code: Yup.string()
        .required("Please input country code."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllCountries()
    fetchDetailContact()
  }, [id]);

  useEffect(() => {
    if (contactDetail && countryCodes) {
      let phoneCode = getPhoneCode(contactDetail?.wa_id, countryCodes)
      if (phoneCode) {
        let numberWithoutCode = contactDetail?.wa_id?.replace(phoneCode?.replace('+', ''), '')
        formik.setFieldValue('contact_wa_id', numberWithoutCode)
        formik.setFieldValue('contact_phone_code', phoneCode)
        let countryData = countryOptions.filter(item => item?.value && JSON.parse(item?.value)?.phone_code === phoneCode)[0]
        if (countryData) {
          setCountry(countryData?.value)
        }
      }
    }
  }, [contactDetail, countryCodes, countryOptions])

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/whatsapp/v1/countries",
    })
      .then(({ data }) => {
        let codeList = data?.map((d) => d?.phone_code)
        setCountryCodes(codeList)
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  };

  const fetchDetailContact = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/whatsapp/v1/contact/id/${id}`,
    })
      .then(({ data }) => {
        if (data) {
          setContactDetail(data?.value)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleInputValue = (values) => {
    let data = {
      profile_name: values.contact_profile_name,
      wa_id: values.contact_phone_code ? `${values.contact_phone_code?.replace('+', '')}${values.contact_wa_id}` : '',
      company_name: values.contact_company_name,
    }
    
    return data
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/whatsapp/v1/contact/update/${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          showSuccess('Contact has been successfully updated')
          navigate('/contacts')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleSelectCountry = (data) => {
    formik.setValues((values) => ({
      ...values,
      contact_phone_code: data ? JSON.parse(data?.value).phone_code : ''
    }), false)

    setCountry(data ? data?.value : null)
  }
  
  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want to edit this contact?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Edit Contact</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            countryOptions={countryOptions}
            setCountry={setCountry}
            country={country}
            handleSelectCountry={handleSelectCountry}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/contacts')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default EditContact